import React, { useEffect, useState } from 'react';
import Head from '../../../layout/head/Head';
import Content from '../../../layout/content/Content';
import {
  Block, BlockBetween, BlockDes, BlockHead, BlockHeadContent,
  BlockTitle, Button, DataTableHead, DataTableItem, DataTableRow, Icon, PaginationComponent
} from '../../../components/Component';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AuthTenantLoader from '../../loaders/AuthTenantLoader';
import AuthTenantPageError from '../../error/AuthTenantPageError';
import AuthTenantNoData from '../../error/AuthTenantNoData';
import { RETRIEVE_TENANT_STOCK_LEVELS_API } from '../../../api/api_data';
import {
  fail_retrieve_stock_levels, loading_retrieve_stock_levels,
  reset_retrieve_stock_levels, success_retrieve_stock_levels
} from '../../../reducers/auth_tenant/retrieve_stock_levels_Slice';
import {
  TENANT_RETRIEVE_RETRIEVE_STOCK_LEVELS_ERROR_MSG,
  TENANT_RETRIEVE_RETRIEVE_STOCK_LEVELS_SUCCESS_MSG
} from '../../../utils/error_success_messages';
import { formatNumber, formatToSentenceCase, messageToast } from '../../../utils/Utils';
import { tenant_auth_actions_helper } from '../../../actions/actions_helper';
import * as app_consts from "../../../utils/app_consts";
import { Card, Col, Row } from 'reactstrap';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import moment from 'moment';
import { motion } from 'framer-motion';

const StockLevelsManagement = ({ tenant_info }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pageData, setPageData] = useState({});
  const [itemPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const retrieve_stock_levels_data = () => {
    setError("");
    setLoading(true);
    dispatch(
      tenant_auth_actions_helper(
        loading_retrieve_stock_levels,
        success_retrieve_stock_levels,
        fail_retrieve_stock_levels,
        TENANT_RETRIEVE_RETRIEVE_STOCK_LEVELS_ERROR_MSG,
        TENANT_RETRIEVE_RETRIEVE_STOCK_LEVELS_SUCCESS_MSG,
        app_consts.GET,
        `/${tenant_info?.url_prefix}/${RETRIEVE_TENANT_STOCK_LEVELS_API}`
      )
    );
  };

  const retrieve_stock_levels_listener = useSelector((state) => state.retrieve_stock_levels.retrieve_stock_levels);

  useEffect(() => {
    retrieve_stock_levels_data();
  }, []);

  useEffect(() => {
    if (retrieve_stock_levels_listener.status !== app_consts.IDLE)
    {
      if (retrieve_stock_levels_listener.status === app_consts.LOADING)
      {
        setLoading(true);
        setError("");
      } else
      {
        if (retrieve_stock_levels_listener.status === app_consts.FAIL)
        {
          setLoading(false);
          setError(retrieve_stock_levels_listener.message);
          messageToast(retrieve_stock_levels_listener.message, app_consts.FAIL);
        } else if (retrieve_stock_levels_listener.status === app_consts.SUCCESS)
        {
          setPageData(retrieve_stock_levels_listener.data || {});
          setTimeout(() => setLoading(false), 500);
        }
        dispatch(reset_retrieve_stock_levels());
      }
    }
  }, [retrieve_stock_levels_listener]);

  const export_to_csv = (category, products) => {
    const cur_dt = moment().format("DD-MM-YYYY h:mm:ss a");
    const csv_headers = ["product_category", "product_name", "product_type", "size", "color", "product_value", "balance"];
    const options = mkConfig({
      fieldSeparator: ',',
      quoteStrings: '"',
      showLabels: true,
      filename: `${formatToSentenceCase(category)} Stock Levels CSV - ${cur_dt}`,
      columnHeaders: csv_headers,
    });
    const csv = generateCsv(options)(products || []);
    download(options)(csv);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = pageData?.grouped_products_list?.slice(indexOfFirstItem, indexOfLastItem) || [];
  const allProducts = pageData?.grouped_products_list?.flatMap(c => c.products) || [];

  return (
    <>
      <Head title={tenant_info?.name ? `${tenant_info.name} Stock Levels Management` : "Stock Levels Management"} />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Stock Levels Management</BlockTitle>
              {!loading && (
                <div className="mt-2">
                  <Link onClick={(e) => { e.preventDefault(); retrieve_stock_levels_data(); }}>
                    <Button color="light" outline className="bg-white d-none d-sm-inline-flex">Refresh</Button>
                  </Link>
                </div>
              )}
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        {loading ? (
          <AuthTenantLoader tenant_info={tenant_info} loading_message="Loading Stock Management Info..." />
        ) : error ? (
          <AuthTenantPageError tenant_info={tenant_info} error_message={error} />
        ) : !pageData || !pageData.grouped_products_list ? (
          <AuthTenantNoData tenant_info={tenant_info} no_data_message="No Data Found" />
        ) : (
          <Block style={{ marginTop: '20px' }}>
            <Row className="g-gs" style={{ marginBottom: '20px' }}>

              {/* Stats Cards with bold numbers */}
              {[{
                label: 'Total Products in Stock', value: pageData?.unique_product_count || "0", icon: "box"
              }, {
                label: 'Total Units', value: `${formatNumber(pageData?.total_units || 0)} Units`, icon: "bar-chart"
              }, {
                label: 'Total Stock Value', value: `${formatNumber(pageData?.total_product_value || 0)} ZMW`, icon: "coin-alt"
              }, {
                label: 'Total Sizes', value: pageData?.size_count || "0", icon: "expand"
              }].map((stat, idx) => (
                <Col xxl="3" sm="6" key={idx} style={{ marginBottom: '20px' }}>
                  <motion.div whileHover={{ scale: 1.03 }} transition={{ duration: 0.3 }}>
                    <Card style={{
                      borderRadius: '12px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
                      height: '100%',
                      padding: '20px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <h6 style={{ fontSize: '14px', color: '#6b7280', marginBottom: '8px' }}>{stat.label}</h6>
                          <h2 style={{ fontSize: '26px', fontWeight: '700', color: '#111827', margin: 0 }}>{stat.value}</h2>
                        </div>
                        <div style={{
                          width: '48px', height: '48px', borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '24px', backgroundColor: 'rgba(37,99,235,0.1)'
                        }}>
                          <Icon name={stat.icon} />
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                </Col>
              ))}

              {/* Brand Distribution */}
              <Col xxl="6" style={{ marginBottom: '20px' }}>
                <Card style={{
                  borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  height: '100%', padding: '20px', backgroundColor: '#fff'
                }}>
                  <h6 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '16px' }}>
                    Brand Distribution {pageData?.grouped_category_percentage ? `(${pageData?.grouped_category_percentage.length} Brands)` : ""}
                  </h6>
                  <Row className="g-gs">
                    {pageData?.grouped_category_percentage?.map((data, i) => (
                      <Col key={i} xxl="6" style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span>{formatToSentenceCase(data?.product_category)}</span>
                          <span style={{ fontWeight: '600', color: '#111827' }}>{formatNumber(data?.percentage || "")}%</span>
                        </div>
                        <div style={{ backgroundColor: '#f1f5f9', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${data?.percentage}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                            borderRadius: '4px'
                          }}></div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>

              {/* Product Type Distribution */}
              <Col xxl="6" style={{ marginBottom: '20px' }}>
                <Card style={{
                  borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                  height: '100%', padding: '20px', backgroundColor: '#fff'
                }}>
                  <h6 style={{ fontSize: '16px', fontWeight: '500', marginBottom: '16px' }}>
                    Product Type Distribution {pageData?.grouped_product_type_percentage ? `(${pageData?.grouped_product_type_percentage.length} Product Types)` : ""}
                  </h6>
                  <Row className="g-gs">
                    {pageData?.grouped_product_type_percentage?.map((data, i) => (
                      <Col key={i} xxl="6" style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                          <span>{formatToSentenceCase(data?.product_type)}</span>
                          <span style={{ fontWeight: '600', color: '#111827' }}>{formatNumber(data?.percentage || "")}%</span>
                        </div>
                        <div style={{ backgroundColor: '#f1f5f9', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                          <div style={{
                            width: `${data?.percentage}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #10b981, #059669)',
                            borderRadius: '4px'
                          }}></div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card>
              </Col>

              {/* Products Section with Export All */}
              <Col lg={12} style={{ marginBottom: '20px' }}>
                <BlockBetween className="mb-3">
                  <BlockHeadContent>
                    <BlockTitle page>Products</BlockTitle>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <Button color="success" size="sm" onClick={() => export_to_csv("All Products", allProducts)}>
                      Export All
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>

                {currentItems.map((category, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <Block>
                      <BlockHead size="sm">
                        <BlockBetween className="mb-2">
                          <BlockHeadContent>
                            <h4 style={{ fontWeight: '600', color: '#1f2937' }}>{category?.product_category}</h4>
                            <BlockDes>
                              <p style={{ color: '#6b7280', fontWeight: '600' }}>
                                Total Balance: <span style={{ color: '#111827' }}>{category.total_balance ? `${formatNumber(category.total_balance)} ZMW` : "0.0 ZMW"}</span>
                              </p>
                            </BlockDes>
                          </BlockHeadContent>
                          <BlockHeadContent>
                            <Button color="primary" size="sm" onClick={() => export_to_csv(category?.product_category, category?.products)}>
                              Export CSV
                            </Button>
                          </BlockHeadContent>
                        </BlockBetween>
                      </BlockHead>
                    </Block>
                    <Block>
                      <div className="nk-tb-list is-separate is-medium mb-3">
                        <DataTableHead className="nk-tb-item">
                          <DataTableRow><span className="sub-text">Product Name</span></DataTableRow>
                          <DataTableRow><span className="sub-text">Size</span></DataTableRow>
                          <DataTableRow><span className="sub-text">Color</span></DataTableRow>
                          <DataTableRow><span className="sub-text">Units</span></DataTableRow>
                          <DataTableRow><span className="sub-text">Product ZMW Value / Units</span></DataTableRow>
                          <DataTableRow><span className="sub-text">Total Value (ZMW)</span></DataTableRow>
                        </DataTableHead>
                        {category?.products?.map((product, j) => (
                          <DataTableItem key={j} className="tb-tnx-item">
                            <DataTableRow><span className="title">{product?.product_name || "--"}</span></DataTableRow>
                            <DataTableRow><span className="title">{product?.size || "--"}</span></DataTableRow>
                            <DataTableRow><span className="title">{product?.color || "--"}</span></DataTableRow>
                            <DataTableRow><span className="title" style={{ fontWeight: '600', color: '#111827' }}>{formatNumber(product?.balance || 0)}</span></DataTableRow>
                            <DataTableRow><span className="title" style={{ fontWeight: '600', color: '#111827' }}>{formatNumber(product?.product_value || 0)}</span></DataTableRow>
                            <DataTableRow><span className="title" style={{ fontWeight: '600', color: '#111827' }}>{formatNumber(product?.total_value || 0)}</span></DataTableRow>
                          </DataTableItem>
                        ))}
                      </div>
                    </Block>
                  </motion.div>
                ))}

                {pageData?.grouped_products_list?.length > itemPerPage && (
                  <div className="mt-3">
                    <PaginationComponent
                      itemPerPage={itemPerPage}
                      totalItems={pageData?.grouped_products_list?.length || 0}
                      paginate={paginate}
                      currentPage={currentPage}
                    />
                  </div>
                )}
              </Col>
            </Row>
          </Block>
        )}
      </Content>
    </>
  );
};

export default StockLevelsManagement;
